import {
  FastCutProductShortPrevInfoType,
  FastCutProductShortType,
} from './../service/list'
import { Dispatch, SetStateAction } from 'react'
import { UseFormSetValue, UseFormWatch } from 'react-hook-form'
import {
  BgmFactor,
  IntroFactor,
  OutroFactor,
  PromptGroup,
  StickerLocationType,
  StickerType,
  SubtitleColorType,
  SubtitleType,
  TransitionFactor,
  VoiceGroupFactor,
} from 'service/list'
import { FormValues } from 'utils/type'

/**
 *
 * @param setValue
 * UseFormSetValue
 * 영상 생성 요청에 필요한 기본 데이터 init
 */
export const initSetValue = (
  setValue: UseFormSetValue<FormValues>,
  watch: UseFormWatch<FormValues>
) => {
  setValue('mainTitle', '')
  setValue('voiceId', 0)
  setValue('userVoiceId', 0)
  setValue('bgmId', 0)
  setValue('transitionId', 0)
  setValue('aiToneTypeId', 0)
  setValue('aiPromptTypeId', 0)
  setValue('aiPromptTypeId', 0)
  setValue('thumbnailId', 0)
  setValue('subtitleId', 0)
  setValue('thumbnailHistoryId', 0)
  setValue('thumbnailMethod', '')

  setValue('thumbnailInfo', {
    channelName: '',
    title1: '',
    title2: '',
    title3: '',
    thumbnailFile: [],
  })

  let defaultProduct = {
    link: '',
    productName: '',
    userScript: '',
    files: [],
    extensionCollectId: 0,
    selectedFiles: [],
    selectedDetails: [],
    selectedThumbnails: [],
    selectedVideos: [],
    selectedVideoTotalDuration: 0,
    withShort: 0,
    subtitleFlag: 0,
    bgmFlag: 0,
    purchaseInfoFlag: 0,
    productTemplateId: 0,
    productTitle: '',
    saveProductTemplateId: 0,
    saveProductTitle: '',
  }

  for (let i = 0; i < 3; i++) {
    const newObj = { ...defaultProduct }
    const selectedProductTemplateId = watch(`products.${i}.productTemplateId`)
    newObj.productTemplateId = selectedProductTemplateId ?? 0
    setValue(`products.${i}`, { ...newObj })
  }
}

export const initializeDefaults = ({
  setValue,
  aiPromptGroup,
  voiceList,
  transitionList,
  introList,
  outroList,
  bgmList,
  subTitleList,
  stickerList,
  stickerLocationList,
  subtitleColorList,
  subtitleFontList,
  subtitleSizeList,
  setPromptStatus,
  setToneTooltip,
  setCopyTransitionList,
  setCopyOutroList,
}: {
  setValue: UseFormSetValue<FormValues>
  aiPromptGroup: PromptGroup[] | undefined
  voiceList: VoiceGroupFactor[]
  transitionList: TransitionFactor[] | undefined
  introList: IntroFactor[] | undefined
  outroList: OutroFactor[] | undefined
  bgmList: BgmFactor[] | undefined
  subTitleList: SubtitleType[] | undefined
  stickerList: StickerType[] | undefined
  stickerLocationList: StickerLocationType[] | undefined
  subtitleColorList: SubtitleColorType[] | undefined
  subtitleFontList: SubtitleColorType[] | undefined
  subtitleSizeList: SubtitleColorType[] | undefined
  setPromptStatus: Dispatch<SetStateAction<boolean[]>>
  setToneTooltip: Dispatch<SetStateAction<boolean[]>>
  setCopyTransitionList: Dispatch<SetStateAction<TransitionFactor[]>>
  setCopyOutroList: Dispatch<SetStateAction<OutroFactor[]>>
}) => {
  setSubtitleValueFromList(subTitleList, setValue, 'subtitleId')
  setDefaultValueFromList(subtitleSizeList, setValue, 'subtitleSizeId')
  setDefaultValueFromList(voiceList, setValue, 'voiceId')
  setDefaultValueFromList(introList, setValue, 'introId')
  setDefaultValueFromList(stickerList, setValue, 'stickerId')
  if (aiPromptGroup) {
    setPromptStatus(new Array(aiPromptGroup.length).fill(false))
    const [init] = aiPromptGroup?.filter((el) => el.default === 1)
    setValue('aiPromptTypeId', init.id)
    if (init?.toneGroup) {
      setToneTooltip(new Array(init.toneGroup.length).fill(false))
      setValue('aiToneTypeId', init.toneGroup[0].id)
    }
  }

  if (transitionList) {
    const id = transitionList.filter((el) => el.default === 1)[0]?.id
    setValue('transitionId', id ?? 0)
    setCopyTransitionList(transitionList)
  }

  if (outroList) {
    const id = outroList.filter((el) => el.default === 1)[0]?.id
    setValue('outroId', id ?? 0)
    setCopyOutroList(outroList)
  }

  //! 위에는 default 속성을 가지고 설정  //  아래는 리스트의 첫번째를 default로 설정
  setFirstItemFromList(bgmList, setValue, 'bgmId')
  setFirstItemFromList(subtitleColorList, setValue, 'subtitleColorId')
  setFirstItemFromList(subtitleFontList, setValue, 'subtitleFontId')

  if (stickerLocationList) {
    setValue('stickerLocation', stickerLocationList[0]?.value)
  }
}

export const setPromptGroupDataFromList = (
  promptGroupList: PromptGroup[] | undefined,
  setValue: UseFormSetValue<FormValues>,
  setToneTooltip: Dispatch<SetStateAction<boolean[]>>,
  aiPromptTypeId: number,
  aiToneTypeId: number
) => {
  if (!promptGroupList || promptGroupList.length === 0) return

  const selectedItem = promptGroupList.find((el) => el.id === aiPromptTypeId)

  if (selectedItem && selectedItem.usable === 1 && aiToneTypeId) {
    const selectedTone = selectedItem.toneGroup?.find(
      (el) => el.id === aiToneTypeId
    )
    if (selectedTone) {
      setToneTooltip(new Array(selectedItem.toneGroup?.length).fill(false))
      setValue('aiToneTypeId', selectedTone.id ?? selectedItem.toneGroup[0].id)
    }

    setValue('aiPromptTypeId', selectedItem.id)
  } else {
    const defaultPromptGroup = promptGroupList?.find((el) => el?.default === 1)
    if (defaultPromptGroup) {
      setValue('aiPromptTypeId', defaultPromptGroup.id)
      setToneTooltip(
        new Array(defaultPromptGroup?.toneGroup?.length).fill(false)
      )
      setValue('aiToneTypeId', defaultPromptGroup?.toneGroup[0]?.id)
    }
  }
}

export const setSubtitleValueFromList = (
  subtitleList: SubtitleType[] | undefined,
  setValue: UseFormSetValue<FormValues>,
  planName: string
) => {
  if (planName === '라이트') {
    setDefaultValueFromList(subtitleList, setValue, 'subtitleId')
    return
  }
  const subtitle = subtitleList?.find((el) => el.name === '화면 하단(1~2줄)')
  if (subtitle) {
    setValue('subtitleId', subtitle.id)
  }
}
export const setDefaultValueFromList = <
  T extends { id: number; default: number }
>(
  list: T[] | undefined,
  setValue: UseFormSetValue<FormValues>,
  field: keyof FormValues
) => {
  if (!list) return

  const defaultItem = list.find((el) => el.default === 1)
  if (defaultItem) setValue(field as keyof FormValues, defaultItem.id)
}

export const setVideoDataValueFromList = <
  T extends { id: number; usable: number; default: number }
>(
  list: T[] | undefined,
  setValue: UseFormSetValue<FormValues>,
  field: keyof FormValues,
  selectedId: number
) => {
  if (!list) return

  const selectedItem = list.find((el) => el.id === selectedId)

  if (selectedItem && selectedItem.usable === 1)
    return setValue(field as keyof FormValues, selectedItem.id)

  return setDefaultValueFromList(list, setValue, field)
}

export const setVideoDataValueNoUsable = <
  T extends { id: number; default: number }
>(
  list: T[] | undefined,
  setValue: UseFormSetValue<FormValues>,
  field: keyof FormValues,
  selectedId: number
) => {
  if (!list) return

  if (selectedId) {
    //기존에 있는것 찾아서 넣어주기
    const selectedItem = list.find((el) => el.id === selectedId)
    if (selectedItem)
      return setValue(field as keyof FormValues, selectedItem.id)
  } else {
    // 기존 로컬스토리지에 데이터가 저장이 안되어있어서 찾아서 넣어줘야 하는 경우
    setDefaultValueFromList(list, setValue, field)
    return
  }

  return setDefaultValueFromList(list, setValue, field)
}

export const setVideoDataValueFromListNoUsable = <
  T extends { id: number; usable?: number; default: number }
>(
  list: T[] | undefined,
  setValue: UseFormSetValue<FormValues>,
  field: keyof FormValues,
  selectedId: number
) => {
  if (!list || selectedId === 0) return

  const selectedItem = list.find((el) => el.id === selectedId)

  if (selectedItem) {
    setValue(field as keyof FormValues, selectedItem.id)
    setValue('voiceId', 0)
  }
}

export const setFirstItemFromList = <T extends { id: number }>(
  list: T[] | undefined,
  setValue: UseFormSetValue<FormValues>,
  field: keyof FormValues
) => {
  if (!list || list.length === 0) return
  setValue(field, list[0].id)
}

export const defaultValues: FormValues = {
  mainTitle: '',
  voiceId: 0,
  userVoiceId: 0,
  bgmId: 0,
  transitionId: 0,
  aiToneTypeId: 0,
  aiPromptTypeId: 0,
  thumbnailId: 0,
  subtitleId: 0,
  outroId: 0,
  products: [
    {
      link: '',
      productName: '',
      userScript: '',
      files: [],
      extensionCollectId: 0,
      selectedFiles: [],
      selectedVideos: [],
      selectedThumbnails: [],
      selectedDetails: [],
      selectedVideoTotalDuration: 0,
      withShort: 0,
      subtitleFlag: 0,
      bgmFlag: 0,
      purchaseInfoFlag: 0,
      productTemplateId: 0,
      productTitle: '',
      saveProductTemplateId: 0,
      saveProductTitle: '',
    },
    {
      link: '',
      productName: '',
      userScript: '',
      files: [],
      extensionCollectId: 0,
      selectedFiles: [],
      selectedVideos: [],
      selectedThumbnails: [],
      selectedDetails: [],
      selectedVideoTotalDuration: 0,
      withShort: 0,
      subtitleFlag: 0,
      bgmFlag: 0,
      purchaseInfoFlag: 0,
      productTemplateId: 0,
      productTitle: '',
      saveProductTemplateId: 0,
      saveProductTitle: '',
    },
    {
      link: '',
      productName: '',
      userScript: '',
      files: [],
      extensionCollectId: 0,
      selectedFiles: [],
      selectedVideos: [],
      selectedThumbnails: [],
      selectedDetails: [],
      selectedVideoTotalDuration: 0,
      withShort: 0,
      subtitleFlag: 0,
      bgmFlag: 0,
      purchaseInfoFlag: 0,
      productTemplateId: 0,
      productTitle: '',
      saveProductTemplateId: 0,
      saveProductTitle: '',
    },
  ],
  thumbnailInfo: {
    channelName: '',
    title1: '',
    title2: '',
    title3: '',
    thumbnailFile: [],
  },
  introId: 0,
  includePrice: false,
  includeAdd: false,
  stickerId: 0,
  stickerLocation: 'lt',
  subtitleColorId: 0,
  thumbnailHistoryId: 0,
  thumbnailMethod: '',
  templateTitle: '',
  templateId: 0,
  subtitleSizeId: 0,
  subtitleFontId: 0,
}

function omit<T extends object, K extends keyof T>(obj: T, key: K): Omit<T, K> {
  const { [key]: _, ...rest } = obj
  return rest
}
export const loadProductShortData = (
  productShortData: FastCutProductShortPrevInfoType,
  setValue: UseFormSetValue<FormValues>,
  aiPromptGroup: PromptGroup[] | undefined,
  voiceList: VoiceGroupFactor[],
  userVoiceList: VoiceGroupFactor[] | undefined,
  bgmList: BgmFactor[] | undefined,
  subTitleList: SubtitleType[] | undefined,
  subtitleColorList: SubtitleColorType[] | undefined,
  subtitleFontList: SubtitleColorType[] | undefined,
  subtitleSizeList: SubtitleColorType[] | undefined,
  setPromptStatus: Dispatch<SetStateAction<boolean[]>>,
  setToneTooltip: Dispatch<SetStateAction<boolean[]>>
) => {
  if (productShortData) {
    const {
      aiPromptTypeId,
      aiToneTypeId,
      subtitleColorId,
      subtitleFontId,
      subtitleId,
      subtitleSizeId,
      voiceId,
      userVoiceId,
      bgmId,
      includePrice,
    } = productShortData
    setPromptGroupDataFromList(
      aiPromptGroup,
      setValue,
      setToneTooltip,
      aiPromptTypeId,
      aiToneTypeId
    )
    setVideoDataValueFromList(voiceList, setValue, 'voiceId', Number(voiceId))
    setVideoDataValueFromList(
      subTitleList,
      setValue,
      'subtitleId',
      Number(subtitleId)
    )
    setVideoDataValueFromListNoUsable(
      userVoiceList,
      setValue,
      'userVoiceId',
      Number(userVoiceId)
    )
    setVideoDataValueNoUsable(
      subtitleFontList,
      setValue,
      'subtitleFontId',
      subtitleFontId
    )
    setVideoDataValueNoUsable(
      subtitleSizeList,
      setValue,
      'subtitleSizeId',
      subtitleSizeId
    )
    setValue('bgmId', bgmId)
    setValue('includePrice', includePrice)
    setValue('subtitleColorId', subtitleColorId)
  } else {
    // 일단 최초 저장된 정보가 없을때 초기화
    //목소리, 나레이션 자막,배경음악 을 초기화 해주면 된다 .
    setSubtitleValueFromList(subTitleList, setValue, 'subtitleId')
    setDefaultValueFromList(subtitleSizeList, setValue, 'subtitleSizeId')
    setDefaultValueFromList(voiceList, setValue, 'voiceId')
    if (aiPromptGroup) {
      setPromptStatus(new Array(aiPromptGroup.length).fill(false))
      const [init] = aiPromptGroup?.filter((el) => el.default === 1)
      setValue('aiPromptTypeId', init.id)
      if (init?.toneGroup) {
        setToneTooltip(new Array(init.toneGroup.length).fill(false))
        setValue('aiToneTypeId', init.toneGroup[0].id)
      }
    }
    setFirstItemFromList(bgmList, setValue, 'bgmId')
    setFirstItemFromList(subtitleColorList, setValue, 'subtitleColorId')
    setFirstItemFromList(subtitleFontList, setValue, 'subtitleFontId')
  }
}
